import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClientInstance } from "@/lib/query-client";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import PageNotFound from "./lib/PageNotFound";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { LanguageProvider } from "@/lib/LanguageContext";

import MobileLayout from "@/components/layout/MobileLayout";
import AppInstallPrompt from "@/components/AppInstallPrompt";
import CookieBanner from "@/components/ui/CookieBanner";
import SEOManager from "@/components/SEOManager";
import { BrandMark } from "@/components/BrandLogo";

const Landing = React.lazy(() => import("@/pages/Landing"));
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const AdminDashboard = React.lazy(() => import("@/pages/AdminDashboard"));
const CreateEvent = React.lazy(() => import("@/pages/CreateEvent"));
const CheckoutSuccess = React.lazy(() => import("@/pages/CheckoutSuccess"));
const JoinEvent = React.lazy(() => import("@/pages/JoinEvent"));
const EventPage = React.lazy(() => import("@/pages/EventPage"));
const Profile = React.lazy(() => import("@/pages/Profile"));
const Explore = React.lazy(() => import("@/pages/Explore"));
const Notifications = React.lazy(() => import("@/pages/Notifications.jsx"));
const SupportTickets = React.lazy(() => import("@/pages/SupportTickets"));
const OrganizerDashboard = React.lazy(() => import("@/pages/OrganizerDashboard"));
const GiftFund = React.lazy(() => import("@/pages/GiftFund"));
const QRCodeShare = React.lazy(() => import("@/pages/QRCodeShare"));
const LiveWall = React.lazy(() => import("@/pages/LiveWall"));
const PublicLiveWall = React.lazy(() => import("@/pages/PublicLiveWall"));
const GuestView = React.lazy(() => import("@/pages/GuestView"));
const GuestEventPage = React.lazy(() => import("@/pages/GuestEventPage"));
const PrivacyPolicy = React.lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("@/pages/TermsOfService"));
const FAQ = React.lazy(() => import("@/pages/FAQ"));
const Blog = React.lazy(() => import("@/pages/Blog"));
const CommunityRules = React.lazy(() => import("@/pages/CommunityRules"));
const StatusSupport = React.lazy(() => import("@/pages/StatusSupport"));
const AuthPage = React.lazy(() => import("@/pages/Auth"));

const FullscreenLoader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-background">
    <div className="space-y-4 text-center">
      <BrandMark className="mx-auto h-14 w-14 shadow-brand" />
      <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto" />
    </div>
  </div>
);

const AppCrashFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background px-6">
    <div className="max-w-md text-center space-y-4">
      <h1 className="text-2xl font-semibold">Something went wrong / Une erreur est survenue</h1>
      <p className="text-muted-foreground">
        The error has been logged. Reload the page to continue. — L'erreur a été enregistrée. Rechargez pour reprendre.
      </p>
      <button
        type="button"
        className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-primary-foreground"
        onClick={() => window.location.reload()}
      >
        Recharger
      </button>
    </div>
  </div>
);

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    import("@/lib/monitoring").then((monitoring) => {
      monitoring.captureException(error, errorInfo);
    });
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

function RequireAuth({ children }) {
  const { isAuthenticated, isLoadingAuth, navigateToLogin } = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    if (!isLoadingAuth && !isAuthenticated) {
      navigateToLogin(`${window.location.origin}${location.pathname}${location.search}`);
    }
  }, [isAuthenticated, isLoadingAuth, location.pathname, location.search, navigateToLogin]);

  if (isLoadingAuth || !isAuthenticated) {
    return <FullscreenLoader />;
  }

  return children;
}

const AuthenticatedApp = () => {
  const { isLoadingAuth } = useAuth();

  if (isLoadingAuth) {
    return <FullscreenLoader />;
  }

  return (
    <React.Suspense fallback={<FullscreenLoader />}>
      <Routes>
        <Route path="/welcome" element={<Landing />} />
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/join" element={<JoinEvent />} />
        <Route path="/guest/:token" element={<GuestView />} />
        <Route path="/guest-event/:sessionToken" element={<GuestEventPage />} />
        <Route path="/screen/:token" element={<PublicLiveWall />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<Blog />} />
        <Route path="/rules" element={<CommunityRules />} />
        <Route path="/status" element={<StatusSupport />} />

        <Route element={<MobileLayout />}>
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AdminDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/create"
            element={
              <RequireAuth>
                <CreateEvent />
              </RequireAuth>
            }
          />
          <Route
            path="/checkout/success"
            element={
              <RequireAuth>
                <CheckoutSuccess />
              </RequireAuth>
            }
          />
          <Route
            path="/explore"
            element={
              <RequireAuth>
                <Explore />
              </RequireAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <Profile />
              </RequireAuth>
            }
          />
          <Route
            path="/support"
            element={
              <RequireAuth>
                <SupportTickets />
              </RequireAuth>
            }
          />
          <Route
            path="/notifications"
            element={
              <RequireAuth>
                <Notifications />
              </RequireAuth>
            }
          />
          <Route
            path="/event/:id"
            element={
              <RequireAuth>
                <EventPage />
              </RequireAuth>
            }
          />
          <Route
            path="/event/:id/organizer"
            element={
              <RequireAuth>
                <OrganizerDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/event/:id/payment"
            element={
              <RequireAuth>
                <GiftFund />
              </RequireAuth>
            }
          />
          <Route
            path="/event/:id/gift"
            element={
              <RequireAuth>
                <GiftFund />
              </RequireAuth>
            }
          />
          <Route
            path="/event/:id/qr"
            element={
              <RequireAuth>
                <QRCodeShare />
              </RequireAuth>
            }
          />
          <Route
            path="/event/:id/live"
            element={
              <RequireAuth>
                <LiveWall />
              </RequireAuth>
            }
          />
        </Route>

        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </React.Suspense>
  );
};

function RouteAwareAppChrome() {
  const location = useLocation();
  const isScreenRoute = location.pathname.startsWith("/screen/");

  return (
    <>
      <SEOManager />
      <AuthenticatedApp />
      {!isScreenRoute && <AppInstallPrompt />}
      {!isScreenRoute && <CookieBanner />}
      <Toaster />
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <AppErrorBoundary fallback={<AppCrashFallback />}>
            <Router>
              <RouteAwareAppChrome />
            </Router>
          </AppErrorBoundary>
        </QueryClientProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
